import {
  forwardRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type ComponentType,
  type InputHTMLAttributes,
  type ReactNode,
  type SVGProps,
  type SelectHTMLAttributes,
} from 'react';
import { EyeIcon, EyeOffIcon } from './icons.tsx';

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Botones
// ---------------------------------------------------------------------------

// Los colores van siempre en la variante: pasar otro color por className compite con el de la
// variante y gana el que Tailwind haya generado después (así quedó invisible el botón del PIN).
type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'dangerGhost' | 'onDark';
type Size = 'sm' | 'md' | 'lg';

// Brillo de arriba hacia abajo en los botones de color: les saca lo plano sin cambiar el color.
const sheen = '[background-image:linear-gradient(rgb(255_255_255/0.14),rgb(255_255_255/0))]';

const variants: Record<Variant, string> = {
  primary: `bg-brand text-brand-fg shadow-card ${sheen} hover:brightness-110 active:brightness-95`,
  secondary: 'bg-white text-slate-800 shadow-card ring-1 ring-slate-300/80 hover:bg-slate-50',
  danger: `bg-danger text-on-solid shadow-card ${sheen} hover:brightness-110 active:brightness-95`,
  ghost: 'text-slate-700 hover:bg-slate-100',
  dangerGhost: 'text-red-700 hover:bg-red-50',
  onDark: 'text-slate-300 hover:bg-slate-800',
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-base',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      data-autofocus={rest.autoFocus || undefined}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition enabled:active:scale-[0.98]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Formularios
// ---------------------------------------------------------------------------

// Ancho, alto y color de borde se eligen con props (compact, invalid) o envolviendo el campo en
// un div con el ancho deseado: pasarlos por className compite con los valores por defecto.
const inputBase =
  'block w-full rounded-lg border-0 bg-white px-3 text-slate-900 shadow-xs ring-1 placeholder:text-slate-400 ' +
  'focus:ring-2 focus:ring-brand focus:outline-none disabled:bg-slate-100 disabled:text-slate-500';

interface FieldStyle {
  /** Alto reducido (h-9) para controles en línea. */
  compact?: boolean;
  /** Borde rojo de error. */
  invalid?: boolean;
}

const fieldClasses = ({ compact, invalid }: FieldStyle) =>
  cx(inputBase, compact ? 'h-9 text-sm' : 'h-11 text-base sm:text-sm', invalid ? 'ring-red-400' : 'ring-slate-300');

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldStyle>(function Input(
  { className, compact, invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      data-autofocus={rest.autoFocus || undefined}
      className={cx(fieldClasses({ compact, invalid }), className)}
      {...rest}
    />
  );
});

/** Campo de contraseña con un botón (ojo) para mostrar u ocultar lo que se escribe. */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & FieldStyle>(function PasswordInput(
  { className, compact, invalid, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        aria-invalid={invalid || undefined}
        data-autofocus={rest.autoFocus || undefined}
        className={cx(fieldClasses({ compact, invalid }), 'pr-11', className)}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-slate-500 hover:text-slate-800"
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        aria-pressed={visible}
      >
        {visible ? <EyeOffIcon className="size-5" /> : <EyeIcon className="size-5" />}
      </button>
    </div>
  );
});

export function Select({ className, children, compact, invalid, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & FieldStyle) {
  return (
    <select aria-invalid={invalid || undefined} className={cx(fieldClasses({ compact, invalid }), className)} {...rest}>
      {children}
    </select>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : hint ? (
        <p className="text-sm text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <label htmlFor={id} className="min-w-0">
        <span className="block text-sm font-medium text-slate-800">{label}</span>
        {description && <span className="block text-sm text-slate-500">{description}</span>}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition disabled:opacity-50',
          checked ? 'bg-brand' : 'bg-slate-300',
        )}
      >
        <span
          className={cx(
            'inline-block size-5 rounded-full bg-white shadow transition',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contenedores y mensajes
// ---------------------------------------------------------------------------

/** flush: sin relleno interno (para listas con divisores que llegan al borde). */
export function Card({ className, children, flush }: { className?: string; children: ReactNode; flush?: boolean }) {
  return (
    <div className={cx('rounded-xl bg-white shadow-card ring-1 ring-slate-200/80', !flush && 'p-4 sm:p-5', className)}>{children}</div>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

const statTones = {
  brand: 'bg-brand/10 text-brand',
  green: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  amber: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  red: 'bg-red-50 text-red-700 ring-1 ring-red-200',
  sky: 'bg-sky-50 text-sky-800 ring-1 ring-sky-200',
  slate: 'bg-slate-100 text-slate-600',
};

/** Número destacado (tableros y resúmenes): título, valor grande y una línea opcional abajo. */
export function Stat({
  label,
  value,
  foot,
  icon: Icon,
  tone = 'brand',
  className,
}: {
  label: string;
  value: ReactNode;
  foot?: ReactNode;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  tone?: keyof typeof statTones;
  className?: string;
}) {
  return (
    <div className={cx('min-w-0 rounded-xl bg-white p-3.5 shadow-card ring-1 ring-slate-200/80 sm:p-4', className)}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-500">{label}</p>
        {Icon && (
          <span className={cx('-mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg', statTones[tone])}>
            <Icon className="size-4" />
          </span>
        )}
      </div>
      <p className="text-xl font-semibold tracking-tight text-slate-900 tabular-nums sm:mt-1 sm:text-2xl">{value}</p>
      {foot && <div className="mt-1 text-xs text-slate-500">{foot}</div>}
    </div>
  );
}

/** Iniciales de un nombre («Laura Gómez» → «LG»). */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase();
}

/** Círculo con las iniciales de una persona o cliente. */
export function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cx('flex size-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 ring-1 ring-slate-200', className)}
    >
      {initials(name)}
    </span>
  );
}

/** Tecla (atajos de teclado). */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex min-w-5 items-center justify-center rounded-md bg-white px-1.5 py-px font-sans text-[11px] font-semibold text-slate-600 shadow-[inset_0_-1px_0_var(--color-slate-300)] ring-1 ring-slate-300">
      {children}
    </kbd>
  );
}

/** Título chico de una sección dentro de una tarjeta o lista. */
export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-2">
      <h2 className="font-semibold tracking-tight text-slate-900">{children}</h2>
      {action}
    </div>
  );
}

const alertStyles = {
  info: 'bg-sky-50 text-sky-900 ring-sky-200',
  success: 'bg-emerald-50 text-emerald-900 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-900 ring-amber-200',
  error: 'bg-red-50 text-red-900 ring-red-200',
};

export function Alert({
  tone = 'info',
  title,
  children,
}: {
  tone?: keyof typeof alertStyles;
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cx('rounded-lg p-3 text-sm ring-1', alertStyles[tone])}>
      {title && <p className="font-medium">{title}</p>}
      {children && <div className={cx(title && 'mt-1')}>{children}</div>}
    </div>
  );
}

export function Badge({ tone = 'slate', children }: { tone?: 'slate' | 'green' | 'amber' | 'red' | 'brand'; children: ReactNode }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200',
    green: 'bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-200',
    amber: 'bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-200',
    red: 'bg-red-100 text-red-800 ring-1 ring-inset ring-red-200',
    brand: 'bg-brand text-brand-fg',
  };
  return <span className={cx('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>{children}</span>;
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className ?? 'size-5')} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

export function FullScreenMessage({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="app-canvas flex min-h-full items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <h1 className="text-lg font-semibold">{title}</h1>
        {children && <div className="mt-3 space-y-3 text-sm text-slate-600">{children}</div>}
      </Card>
    </div>
  );
}

/** Mensaje de error amigable a partir de cualquier excepción. */
export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Ocurrió un error inesperado.';
}
